// Minimap (M5, DESIGN.md §11): bottom-left canvas — terrain, territory tint,
// fog, building/unit dots, camera viewport polygon, alert pings. Click or drag
// to jump the camera. Base layer (terrain+territory+fog) is rebuilt at most
// every 400 ms into an offscreen canvas; dots/viewport redraw every frame.

import * as THREE from 'three';
import { FogState, Owner, Terrain, UnitKind } from '../shared/constants';
import { BUILDING_STRIDE, UNIT_STRIDE } from '../shared/protocol';
import type { GameScene } from '../render/scene';

const SIZE = 192; // css pixels
const BASE_INTERVAL_MS = 400;

const TERRAIN_RGB: Record<number, [number, number, number]> = {
  [Terrain.Water]: [34, 62, 92],
  [Terrain.Sand]: [150, 136, 96],
  [Terrain.Grass]: [70, 106, 58],
  [Terrain.Forest]: [44, 78, 44],
  [Terrain.Rock]: [104, 100, 96],
};

/** territory tint per owner (player green / enemy red), blended over terrain */
const OWNER_RGB: Record<number, [number, number, number]> = {
  [Owner.Player]: [90, 200, 120],
  [Owner.Enemy]: [220, 80, 70],
  [Owner.Bandit]: [180, 140, 60],
};

const OWNER_DOT: Record<number, string> = {
  [Owner.Player]: '#8fff9f',
  [Owner.Enemy]: '#ff6f5f',
  [Owner.Bandit]: '#e0a050',
};

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx2d: CanvasRenderingContext2D;
  private base: HTMLCanvasElement;
  private baseCtx: CanvasRenderingContext2D;
  private baseImage: ImageData | null = null;
  private baseBuiltAt = 0;
  private baseDirty = true;
  private mapW = 0;
  private mapH = 0;
  private pings: Array<{ x: number; y: number; until: number }> = [];
  private dragging = false;
  private ray = new THREE.Raycaster();
  private ndc = new THREE.Vector2();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private hitPoint = new THREE.Vector3();

  constructor(
    parent: HTMLElement,
    private onJump: (x: number, y: number) => void,
  ) {
    const wrap = document.createElement('div');
    wrap.id = 'minimap';
    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    wrap.appendChild(this.canvas);
    parent.appendChild(wrap);
    this.ctx2d = this.canvas.getContext('2d')!;
    this.base = document.createElement('canvas');
    this.baseCtx = this.base.getContext('2d')!;

    const jump = (e: PointerEvent): void => {
      if (this.mapW === 0) return;
      const rect = this.canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * this.mapW;
      const y = ((e.clientY - rect.top) / rect.height) * this.mapH;
      this.onJump(Math.min(this.mapW, Math.max(0, x)), Math.min(this.mapH, Math.max(0, y)));
    };
    this.canvas.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this.dragging = true;
      this.canvas.setPointerCapture(e.pointerId);
      jump(e);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (this.dragging) jump(e);
    });
    this.canvas.addEventListener('pointerup', () => {
      this.dragging = false;
    });
  }

  /** call when a new map arrives */
  reset(mapW: number, mapH: number): void {
    this.mapW = mapW;
    this.mapH = mapH;
    this.base.width = mapW;
    this.base.height = mapH;
    this.baseImage = this.baseCtx.createImageData(mapW, mapH);
    this.baseDirty = true;
    this.pings.length = 0;
  }

  /** territory/fog/terrain changed — rebuild the base layer soon */
  markDirty(): void {
    this.baseDirty = true;
  }

  ping(x: number, y: number): void {
    this.pings.push({ x, y, until: performance.now() + 6000 });
    if (this.pings.length > 8) this.pings.shift();
  }

  private rebuildBase(scene: GameScene): void {
    const { terrainData, fogData, hexOfTile, hexOwner } = scene;
    if (!terrainData || !hexOfTile || !hexOwner || !this.baseImage) return;
    const px = this.baseImage.data;
    const n = this.mapW * this.mapH;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const fog = fogData ? fogData[i] : FogState.Visible;
      if (fog === FogState.Unexplored) {
        px[o] = 8;
        px[o + 1] = 10;
        px[o + 2] = 13;
        px[o + 3] = 255;
        continue;
      }
      const t = TERRAIN_RGB[terrainData[i]] ?? TERRAIN_RGB[Terrain.Grass];
      let r = t[0];
      let g = t[1];
      let b = t[2];
      const owner = hexOwner[hexOfTile[i]];
      const tint = OWNER_RGB[owner];
      if (tint) {
        r = (r * 3 + tint[0]) >> 2;
        g = (g * 3 + tint[1]) >> 2;
        b = (b * 3 + tint[2]) >> 2;
      }
      if (fog === FogState.Explored) {
        r = (r * 5) >> 3;
        g = (g * 5) >> 3;
        b = (b * 5) >> 3;
      }
      px[o] = r;
      px[o + 1] = g;
      px[o + 2] = b;
      px[o + 3] = 255;
    }
    this.baseCtx.putImageData(this.baseImage, 0, 0);
  }

  /** draw the camera's ground-plane viewport as a polygon */
  private drawViewport(scene: GameScene, sx: number, sy: number): void {
    if (!scene.cam) return;
    const cam = scene.cam.camera;
    const corners: Array<[number, number]> = [];
    for (const [nx, ny] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as Array<[number, number]>) {
      this.ndc.set(nx, ny);
      this.ray.setFromCamera(this.ndc, cam);
      if (this.ray.ray.intersectPlane(this.groundPlane, this.hitPoint)) {
        corners.push([this.hitPoint.x * sx, this.hitPoint.z * sy]);
      }
    }
    if (corners.length !== 4) return;
    const c = this.ctx2d;
    c.strokeStyle = 'rgba(255,255,255,0.75)';
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(corners[0][0], corners[0][1]);
    for (let i = 1; i < 4; i++) c.lineTo(corners[i][0], corners[i][1]);
    c.closePath();
    c.stroke();
  }

  update(scene: GameScene): void {
    if (this.mapW === 0) return;
    const now = performance.now();
    if (this.baseDirty && now - this.baseBuiltAt > BASE_INTERVAL_MS) {
      this.baseDirty = false;
      this.baseBuiltAt = now;
      this.rebuildBase(scene);
    }
    const c = this.ctx2d;
    c.imageSmoothingEnabled = false;
    c.drawImage(this.base, 0, 0, SIZE, SIZE);
    const sx = SIZE / this.mapW;
    const sy = SIZE / this.mapH;

    // buildings: small owner-colored squares (snapshot already fog-filtered)
    const b = scene.buildingsSnapshot;
    if (b) {
      for (let i = 0; i < b.count; i++) {
        const o = i * BUILDING_STRIDE;
        const owner = b.buildings[o + 10];
        c.fillStyle = OWNER_DOT[owner] ?? '#cccccc';
        const size = Math.max(2, b.buildings[o + 4] * sx);
        c.fillRect(b.buildings[o + 2] * sx, b.buildings[o + 3] * sy, size, size);
      }
    }
    // units: 1-2px dots; civilians dimmer than military
    const u = scene.unitsSnapshot;
    if (u) {
      for (let i = 0; i < u.count; i++) {
        const o = i * UNIT_STRIDE;
        const kind = u.units[o + 1];
        const owner = u.units[o + 7];
        const military = kind >= UnitKind.Scout;
        c.fillStyle = military ? (OWNER_DOT[owner] ?? '#cccccc') : 'rgba(230,220,170,0.6)';
        const d = military ? 2 : 1;
        c.fillRect(u.units[o + 2] * sx - d / 2, u.units[o + 3] * sy - d / 2, d, d);
      }
    }
    // alert pings: expanding rings
    this.pings = this.pings.filter((p) => p.until > now);
    for (const p of this.pings) {
      const t = 1 - (p.until - now) / 6000;
      const r = 3 + ((t * 1000) % 1000) / 90;
      c.strokeStyle = `rgba(255,90,70,${(0.9 - ((t * 1000) % 1000) / 1400).toFixed(2)})`;
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(p.x * sx, p.y * sy, r, 0, Math.PI * 2);
      c.stroke();
    }
    this.drawViewport(scene, sx, sy);
  }
}
